/**
 * OCR and text extraction from screen capture images.
 * - Local: Tesseract.js (client-side).
 * - Code block detection and Markdown formatting.
 * - Simple language detection (keyword-based for code vs natural language).
 */

const CODE_INDICATORS = [
  /\b(function|const|let|var|def |class |import |from |return |=>|{|}|\[|\]|\(\)|\.py|\.tsx?|\.jsx?)\b/,
  /^\s{2,}/m,
  /```/,
  /[{}();[\]]/,
];

const LANG_KEYWORDS: Record<string, RegExp[]> = {
  python: [/\bdef\s+\w+\s*\(/, /\bimport\s+\w+/, /\bfrom\s+\w+\s+import/, /\.py\b/],
  javascript: [/\b(function|const|let|var)\s*\(/, /=>/, /\.jsx?\b/],
  typescript: [/\binterface\s+\w+/, /\btype\s+\w+\s*=/, /\.tsx?\b/],
};

/**
 * Wrap text that looks like code in Markdown code blocks and guess language.
 */
export function formatCodeBlocks(text: string): { text: string; language?: string } {
  const trimmed = text.trim();
  if (!trimmed) return { text: trimmed };

  const looksLikeCode = CODE_INDICATORS.some((r) => r.test(trimmed));
  if (!looksLikeCode) return { text: trimmed };

  let language: string | undefined;
  for (const [lang, patterns] of Object.entries(LANG_KEYWORDS)) {
    if (patterns.some((p) => p.test(trimmed))) {
      language = lang;
      break;
    }
  }

  const codeBlock = language
    ? `\n\`\`\`${language}\n${trimmed}\n\`\`\`\n`
    : `\n\`\`\`\n${trimmed}\n\`\`\`\n`;
  return { text: codeBlock, language };
}

/**
 * Detect if extracted text is likely natural language (en/es) or code.
 */
export function detectTextKind(text: string): "code" | "natural" {
  const t = text.trim();
  if (CODE_INDICATORS.some((r) => r.test(t))) return "code";
  return "natural";
}

/**
 * Run Tesseract.js on an image data URL and return extracted text.
 * Uses optional lang for "eng", "spa", etc.
 */
export async function extractTextWithTesseract(
  imageDataUrl: string,
  lang: string = "eng"
): Promise<string> {
  const Tesseract = await import("tesseract.js");
  const {
    data: { text },
  } = await Tesseract.recognize(imageDataUrl, lang, {
    logger: () => {},
  });
  return text.trim();
}

/**
 * Full OCR pipeline: extract text, detect kind, optionally format as code block.
 */
export async function extractTextFromImage(
  imageDataUrl: string,
  options: { formatCode?: boolean; lang?: string } = {}
): Promise<{ text: string; language?: string; kind: "code" | "natural" }> {
  const { formatCode = true, lang = "eng" } = options;
  const raw = await extractTextWithTesseract(imageDataUrl, lang);
  const kind = detectTextKind(raw);
  if (formatCode && kind === "code") {
    const { text, language } = formatCodeBlocks(raw);
    return { text, language, kind };
  }
  return { text: raw, kind };
}
